// import { useDebounce } from '@/hooks/useDebounce';
import PropTypes from "prop-types";
import React, { useState, useRef, useEffect } from "react";
import { useDebounce } from "../../../hook/useDebounce";
import { reactIcons } from "../../../utils/icons";

const CustomSelect = ({
  name = "customSelect",
  options = [],
  placeholder = "Select an option",
  onChange = () => {},
  defaultValue,
  className = "",
  placeholderClassName = "text-gray-400",
  selectClassName = "",
  dropdownClassName = "",
  optionClassName = "",
  arrowClassName = "",
  selectedOptionClassName = "",
  disabled = false,
  value,
  onScrollEnd = () => {},
  loadOptions
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [openDirection, setOpenDirection] = useState("down");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [showHighlightedIndex, setShowHighltedIndex] = useState(true);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const lastSearch = useRef("");

  useEffect(() => {
    if (
      loadOptions &&
      lastSearch.current !== debouncedSearchTerm &&
      searchTerm === debouncedSearchTerm
    ) {
      lastSearch.current = debouncedSearchTerm;
      loadOptions(debouncedSearchTerm);
    }
    // eslint-disable-next-line
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const match = options.find((opt) => opt.value === value);
    if (match) {
      setSelected(match);
    }
  }, [value, options]);

  const handleSelect = (option) => {
    if (disabled) return;
    setSelected(option);
    onChange({
      target: {
        name,
        value: option.value,
        type: "select-one"
      }
    });
    setIsOpen(false);
    setSearchTerm("");
    setHighlightedIndex(0);
  };

  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = loadOptions
    ? options
    : options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );

  const handleOpen = () => {
    if (disabled) return;

    const rect = dropdownRef.current?.getBoundingClientRect();
    const dropdownHeight = 240;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    setOpenDirection(
      spaceBelow < dropdownHeight && spaceAbove > dropdownHeight ? "up" : "down"
    );
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % filteredOptions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex(
        (prev) => (prev - 1 + filteredOptions.length) % filteredOptions.length
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredOptions[highlightedIndex]) {
        handleSelect(filteredOptions[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const optionRefs = useRef([]);

  useEffect(() => {
    if (isOpen && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth"
      });
    }
  }, [highlightedIndex, isOpen]);

  return (
    <div
      ref={dropdownRef}
      className={`relative ${className} ${
        disabled ? "opacity-50 cursor-default" : ""
      }`}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div
        className={`border border-gray-300 rounded-lg px-4 py-2.5 flex items-center justify-between 
    ${disabled ? "bg-gray-300 cursor-not-allowed" : "bg-white cursor-pointer"} 
    ${selectClassName}`}
        onClick={disabled ? undefined : handleOpen}
      >
        <span className={selected ? "" : placeholderClassName}>
          {selected ? selected.label : placeholder}
        </span>
        <span
          className={`ml-2 text-xl transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          } ${arrowClassName}`}
        >
          {reactIcons.arrowDown}
        </span>
      </div>

      {isOpen && !disabled && (
        <div
          className={`absolute ${
            openDirection === "up" ? "bottom-full mb-1" : "top-full mt-1"
          } left-0 w-full border border-gray-300 bg-white rounded-lg z-10 shadow ${dropdownClassName}`}
        >
          <div className="px-3 py-2">
            <input
              ref={searchInputRef}
              type="text"
              className="w-full border border-gray-200 rounded px-2 py-1 text-sm outline-none"
              placeholder="Type to search..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setHighlightedIndex(0);
              }}
            />
          </div>

          <div
            className="max-h-60 overflow-y-auto scrollbar-hide"
            onScroll={(e) => {
              const { scrollTop, scrollHeight, clientHeight } = e.target;
              if (scrollTop + clientHeight >= scrollHeight - 10) {
                onScrollEnd();
              }
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const isSelected = selected?.value === option.value;
                const isHighlighted = index === highlightedIndex;

                return (
                  <div
                    ref={(el) => (optionRefs.current[index] = el)}
                    name={name}
                    key={option.value}
                    className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                      isSelected ? `bg-gray-200 ${selectedOptionClassName}` : ""
                    } ${
                      isHighlighted && showHighlightedIndex
                        ? `bg-blue-100 ${selectedOptionClassName}`
                        : ""
                    } ${optionClassName}`}
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => setShowHighltedIndex(false)}
                    onMouseLeave={() => setShowHighltedIndex(true)}
                  >
                    {option.label}
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-2 text-gray-400">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

CustomSelect.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired
    })
  ).isRequired,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  defaultValue: PropTypes.any,
  className: PropTypes.string,
  placeholderClassName: PropTypes.string,
  selectClassName: PropTypes.string,
  dropdownClassName: PropTypes.string,
  optionClassName: PropTypes.string,
  arrowClassName: PropTypes.string,
  selectedOptionClassName: PropTypes.string,
  disabled: PropTypes.bool,
  onScrollEnd: PropTypes.func,
  loadOptions: PropTypes.func,
  value: PropTypes.any
};

export default CustomSelect;
